import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, FolderPlus } from 'lucide-react'
import type { CardTicket, ProjectAttention, ProjectCard, ProjectCards } from '@one-deployment/api'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/shadcn/hover-card'
import { Button, Dialog, SkeletonRows, Textarea } from '@/components/ui'
import { Button as SButton } from '@/components/shadcn/button'
import { Age, EmptyState, ErrorState } from '@/components/status'
import { RoleGate } from '@/components/RoleGate'
import { cn } from '@/lib/cn'
import { enc, get, post } from '@/lib/api'
import { useGlobalQueryOpts } from '@/lib/live'
import { qk } from '@/lib/query'
import { CreateProjectButton } from '@/features/projects/CreateProject'
import { SetupsInProgress } from '@/features/projects/NewFromTemplate'
import { Owner, PriorityIcon } from '@/features/projects/Tickets'
import { AlertRow } from '@/features/alerts/Alerts'

/** Home: one card per project you can see (every project for an admin), each opening that project. */
export function HomePage() {
  const live = useGlobalQueryOpts(30_000)
  const { data, error, refetch, isLoading } = useQuery({ queryKey: qk.home(), queryFn: ({ signal }) => get<ProjectCards>('/api/home', signal), ...live })

  if (error && !data) return <ErrorState error={error} retry={() => void refetch()} />
  if (isLoading || !data) return <div className="p-5"><SkeletonRows rows={8} /></div>

  if (data.projects.length === 0) {
    return (
      <EmptyState
        icon={<FolderPlus size={22} />}
        title="No projects yet"
        body="A project is one GitHub repo plus its Linear team."
        action={
          <RoleGate action="projects.create" fallback={<span className="text-xs text-fg-subtle">Ask an admin to add you to one.</span>}>
            <CreateProjectButton trigger={<Button variant="primary">Create project</Button>} />
          </RoleGate>
        }
      />
    )
  }

  return (
    <div className="space-y-6 px-6 py-5">
      {/* Client projects first, then internal ones under their own heading, as in the sidebar. */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(20rem,1fr))] gap-3">
        {data.projects.filter((p) => !p.internal).map((p) => <Card key={p.key} p={p} />)}
        <RoleGate action="projects.create" fallback={null}><SetupsInProgress /></RoleGate>
      </div>
      {data.projects.some((p) => p.internal) && (
        <section className="space-y-2">
          <h2 className="text-xs font-medium text-fg-subtle">Internal</h2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(20rem,1fr))] gap-3">
            {data.projects.filter((p) => p.internal).map((p) => <Card key={p.key} p={p} />)}
          </div>
        </section>
      )}
    </div>
  )
}

function Card({ p }: { p: ProjectCard }) {
  const [open, setOpen] = useState<'needs' | 'alerts' | null>(null)
  // Buttons sit inside the card's link: keep their clicks from opening the project.
  const show = (which: 'needs' | 'alerts') => (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); setOpen(which) }
  return (
    <>
      <Link
        to="/p/$project"
        params={{ project: p.key }}
        className="flex h-full flex-col gap-3 rounded-lg border border-border bg-surface-2 p-4 transition-colors hover:bg-hover"
      >
        {/* Name, then tags (dot + count; hover for the tickets): alerts, needs attention, blocked, client response. */}
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="mr-0.5 truncate font-semibold">{p.name}</span>
          {p.alerts > 0 && <Count n={p.alerts} tone={p.alertLevel === 'down' ? 'err' : 'warn'} title={`${p.alerts} alert${p.alerts === 1 ? '' : 's'}`} onClick={show('alerts')} />}
          <TicketChip label="Needs attention" tone="warn" list={p.tickets.attention} count={p.needsYou} onClick={show('needs')} />
          <TicketChip label="Blocked" tone="err" list={p.tickets.blocked} />
          <TicketChip label="Client response needed" tone="info" list={p.tickets.client} />
          {p.repoState !== 'ok' && <span className="ml-auto shrink-0 rounded-full bg-warn/15 px-2 py-0.5 text-2xs font-medium text-warn">repo missing</span>}
        </div>

        <Cycle c={p.cycle} />
      </Link>
      {open && <Attention project={p} which={open} onClose={() => setOpen(null)} />}
    </>
  )
}

function Count({ n, tone, title, onClick }: { n: number; tone: 'err' | 'warn'; title: string; onClick: (e: React.MouseEvent) => void }) {
  return (
    <SButton variant="ghost" size="xs" title={title} aria-label={title} onClick={onClick}
      className={cn('h-5 shrink-0 gap-1 rounded-full px-1.5 text-xs font-medium tabular-nums', tone === 'err' ? 'bg-err/15 text-err hover:bg-err/25 hover:text-err' : 'bg-warn/15 text-warn hover:bg-warn/25 hover:text-warn')}>
      <span className={cn('size-1.5 rounded-full', tone === 'err' ? 'bg-err' : 'bg-warn')} />{n}
    </SButton>
  )
}

function TicketChip({ label, tone, list, count, onClick }: { label: string; tone: 'warn' | 'err' | 'info'; list: CardTicket[]; count?: number; onClick?: (e: React.MouseEvent) => void }) {
  const n = count ?? list.length
  if (!n) return null
  const color = tone === 'err' ? 'bg-err/15 text-err' : tone === 'warn' ? 'bg-warn/15 text-warn' : 'bg-info/15 text-info'
  const dot = tone === 'err' ? 'bg-err' : tone === 'warn' ? 'bg-warn' : 'bg-info'
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button type="button" aria-label={`${label}: ${n}`} onClick={onClick ?? ((e) => { e.preventDefault(); e.stopPropagation() })} className={cn('flex h-5 shrink-0 items-center gap-1 rounded-full px-1.5 text-xs font-medium tabular-nums', color)}>
          <span className={cn('size-1.5 rounded-full', dot)} />{n}
        </button>
      </HoverCardTrigger>
      {/* Portalled, but React still bubbles its clicks to the card's link: stop them here. */}
      <HoverCardContent align="start" className="w-[48rem] max-w-[90vw] p-1.5" onClick={(e) => e.stopPropagation()}>
        <div className="px-3 py-2 text-xs font-medium text-fg-subtle">{label} · {n}</div>
        <div className="max-h-[32rem] overflow-y-auto">
          {list.length === 0 && <div className="px-2 py-1.5 text-xs text-fg-faint">Click to see them.</div>}
          {sortTickets(list).map((t) => (
            <a key={t.identifier} href={t.url} target="_blank" rel="noreferrer" className="flex h-10 items-center gap-3 rounded-md px-3 text-sm hover:bg-hover">
              <span className="grid size-5 shrink-0 place-items-center rounded-sm border border-border"><PriorityIcon p={t.priority} /></span>
              {t.estimate !== null
                ? <span className="w-12 shrink-0 rounded-sm border border-border text-center text-[11px] leading-[18px] tabular-nums text-fg-subtle">{t.estimate} pt{t.estimate > 1 ? 's' : ''}</span>
                : <span className="w-12 shrink-0 rounded-sm border border-dashed border-border text-center text-[11px] leading-[18px] text-fg-faint">–</span>}
              <span className="min-w-0 flex-1 truncate text-fg">{t.title}</span>
              <Owner name={t.assignee} src={t.assigneeAvatar} />
            </a>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}

/** The team's current cycle: done of total. */
function Cycle({ c }: { c: ProjectCard['cycle'] }) {
  if (!c) return <div className="text-xs text-fg-faint">No active cycle</div>
  const pct = c.total ? Math.round((c.done / c.total) * 100) : 0
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-fg-muted">{c.name || `Cycle ${c.number}`}</span>
        <span className="tabular-nums text-fg-subtle">{c.done}/{c.total} done</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-3"><div className="h-full rounded-full bg-ok" style={{ width: `${pct}%` }} /></div>
    </div>
  )
}



/** The card's "needs you" or alerts list, acted on in place. */
function Attention({ project, which, onClose }: { project: ProjectCard; which: 'needs' | 'alerts'; onClose: () => void }) {
  const { data, error, refetch } = useQuery({
    queryKey: ['project', project.key, 'attention'],
    queryFn: ({ signal }) => get<ProjectAttention>(`/api/projects/${enc(project.key)}/attention`, signal),
  })
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`${project.name} · ${which === 'needs' ? 'Needs you' : 'Alerts'}`} width={640}>
      {error && !data ? <ErrorState error={error} retry={() => void refetch()} compact />
        : !data ? <SkeletonRows rows={3} />
        : which === 'needs'
          ? data.needsYou.length === 0 ? <p className="text-sm text-fg-subtle">Nothing needs you now.</p>
            : <div className="-mx-1 max-h-[60vh] space-y-3 overflow-y-auto px-1">{data.needsYou.map((n) => <NeedsRow key={n.workspaceId} project={project.key} n={n} />)}</div>
          : data.alerts.length === 0 ? <p className="text-sm text-fg-subtle">No open alerts.</p>
            : <div className="max-h-[60vh] overflow-y-auto rounded-md border border-border">{data.alerts.map((a) => <AlertRow key={a.id} alert={a} compact />)}</div>}
    </Dialog>
  )
}

function NeedsRow({ project, n }: { project: string; n: ProjectAttention['needsYou'][number] }) {
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const [replying, setReplying] = useState(false)
  const done = () => { void qc.invalidateQueries({ queryKey: ['project', project, 'attention'] }); void qc.invalidateQueries({ queryKey: qk.home() }) }
  const retry = useMutation({ mutationFn: () => post(`/api/jobs/${enc(n.lastJobId ?? '')}/retry`), onSuccess: done })
  const reply = useMutation({ mutationFn: () => post(`/api/workspaces/${enc(n.workspaceId)}/jobs`, { mode: 'reply', text }), onSuccess: () => { setText(''); setReplying(false); done() } })
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-xs text-fg-subtle">{n.ticket}</span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{n.title}</span>
        <Age from={n.updatedAt} className="shrink-0 text-2xs text-fg-faint" />
      </div>
      {n.reason && <p className="mt-2 max-h-40 overflow-y-auto text-xs whitespace-pre-wrap text-fg-muted">{n.reason}</p>}
      {replying && (
        <Textarea className="mt-2" rows={3} autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Answer Claude; the ticket picks up from here." />
      )}
      {(retry.error || reply.error) && <div className="mt-2"><ErrorState error={retry.error ?? reply.error} compact /></div>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {replying
          ? <>
              <Button variant="primary" disabled={!text.trim() || reply.isPending} onClick={() => reply.mutate()}>Send reply</Button>
              <Button onClick={() => setReplying(false)}>Cancel</Button>
            </>
          : <>
              <Button variant="primary" onClick={() => setReplying(true)}>Reply</Button>
              <Button disabled={!n.lastJobId || retry.isPending} onClick={() => retry.mutate()}>{retry.isSuccess ? 'Queued' : 'Retry'}</Button>
            </>}
        <Link to="/p/$project/w/$ticket" params={{ project, ticket: n.ticket }} className="ml-auto text-xs text-accent hover:underline">Open ticket</Link>
        <a href={n.linearUrl} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">Linear</a>
      </div>
    </div>
  )
}

// Linear priority 1 (urgent) to 4 (low); 0 means none and goes last. Ties: biggest estimate first.
const rank = (p: number | null) => (p ? p : 5)
function sortTickets(list: CardTicket[]) {
  return [...list].sort((a, b) => rank(a.priority) - rank(b.priority) || (b.estimate ?? -1) - (a.estimate ?? -1))
}
